import React, {
  useEffect,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import Accordion from 'core/components/Accordion';
import Skeleton from 'core/components/Skeleton';
import AlertCard from 'views/EMR/PatientAlerts/AlertCard';
import {
  useGetPatientAlerts,
} from 'views/EMR/PatientAlerts/hooks';
import {
  patientAlertsEventChannel,
  rtmEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type Part = 'root' | 'header' | 'list' | 'item';

type Props = {
  patientId: number;
  encounterId?: number;
  classes?: Partial<Record<Part, string>>;
  size?: 'small' | 'medium';
};

type AlertsHeaderProps = {
  alertCount: number;
  className?: string;
  hasAlerts: boolean;
  isLoading: boolean;
};

function AlertsHeader({
  alertCount,
  className,
  hasAlerts,
  isLoading,
}: AlertsHeaderProps) {
  const badgeClassName = clsx(
    'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full',
    'text-white text-xs font-semibold',
    {
      'bg-amber-500': hasAlerts,
      'bg-gray-400': !hasAlerts,
    },
  );

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <span className={badgeClassName}>
        {isLoading ? '…' : alertCount}
      </span>
      <span>Alerts</span>
    </div>
  );
}

export default function PatientAlerts({
  patientId,
  encounterId,
  classes,
  size = 'medium',
}: Props) {
  const enabled = patientId > 0;
  const {
    data: alerts,
    isLoading,
    refetch,
  } = useGetPatientAlerts(patientId, encounterId);

  const hasAlerts = !isNil(alerts) && alerts.length > 0;

  useEffect(() => {
    const unsubscribeAlerts = patientAlertsEventChannel.on(
      'onPatientAlertsChanged',
      (targetPatientId) => {
        if (targetPatientId === patientId) {
          refetch();
        }
      },
    );

    const unsubscribeRTM = rtmEventChannel.on(
      'onRTMPatientDataChange',
      (targetPatientId) => {
        if (targetPatientId === patientId) {
          refetch();
        }
      },
    );

    return () => {
      unsubscribeAlerts();
      unsubscribeRTM();
    };
  }, [patientId, refetch]);

  return enabled && hasAlerts ? (
    <Accordion
      defaultExpanded
      disableGutters
      header={(
        <AlertsHeader
          alertCount={alerts?.length ?? 0}
          className={classes?.header}
          hasAlerts={hasAlerts}
          isLoading={isLoading}
        />
      )}
      className={classes?.root}
      headerClasses={{
        root: '!px-2 !py-1 !min-h-[initial] !bg-gray-100 !text-gray-600 !font-semibold text-sm !rounded-t',
        content: '!m-0',
      }}
      detailsClasses={{ root: '!p-0 !flex !flex-col w-full' }}
    >
      {isLoading && (
        <div className={clsx('flex flex-col gap-2 w-full px-2 py-2', classes?.list)}>
          <Skeleton className="w-full !h-10" />
          <Skeleton className="w-full !h-10" />
        </div>
      )}
      {!isLoading && hasAlerts && (
        <div className={clsx('flex flex-col w-full rounded', classes?.list)}>
          {(alerts ?? []).map((alert) => (
            <AlertCard
              key={alert.alertId}
              alert={alert}
              patientId={patientId}
              encounterId={encounterId}
              className={classes?.item}
              size={size}
            />
          ))}
        </div>
      )}
      {!isLoading && !hasAlerts && (
        <p className="text-xs text-gray-500 px-3 py-2">
          No active alerts.
        </p>
      )}
    </Accordion>
  ) : null;
}
